import { z } from 'zod';
import { UserInteractionSchemas } from './user-interactions';
import { ValidationPatterns } from '../shared';

/**
 * USER WORD PROGRESS SCHEMAS - Individual Word Learning Progress
 * =============================================================================
 * Following Base+Variants pattern from docs/schemas-plan.md
 * Core learning progress entity tracking user's journey with individual words
 * Used extensively across all stats routes via UserWordProgressRepository
 * =============================================================================
 */

// Validation patterns for user word progress fields
const patterns = {
  mongoObjectId: ValidationPatterns.flexibleObjectId,
  userId: z.string().min(1), // User identifier
  interactionCount: z.number().int().min(0), // Non-negative interaction count
  isoDateTime: z.iso.datetime(), // ISO 8601 datetime string
};

// =============================================================================
// BASE USER WORD PROGRESS SCHEMA - Individual word learning journey (7 fields)
// =============================================================================
const BaseUserWordProgressSchema = z.object({
  // Entity relationships
  userId: ValidationPatterns.flexibleObjectId,
  wordId: ValidationPatterns.flexibleObjectId, // Word being learned
  collectionId: ValidationPatterns.flexibleObjectId.optional(), // Optional collection context

  // Learning timeline
  dateLearned: ValidationPatterns.flexibleDate, // When word was marked as learned
  lastInteractionAt: ValidationPatterns.flexibleDate, // Most recent interaction

  // Interaction tracking
  interactions: z.array(UserInteractionSchemas.base), // All interactions with this word
  totalInteractions: patterns.interactionCount, // Count of interactions
});

// =============================================================================
// USER WORD PROGRESS VARIANTS - For different usage contexts in stats
// =============================================================================
export const UserWordProgressSchemas = {
  /**
   * FULL: Complete word progress entity (repository/service layer)
   * All 7 fields including interactions array for comprehensive operations
   */
  full: BaseUserWordProgressSchema,
} as const;

// =============================================================================
// TYPE EXPORTS - Clean TypeScript types
// =============================================================================
export type UserWordProgress = z.infer<typeof BaseUserWordProgressSchema>;
// Removed unused SummaryUserWordProgress type
// Removed unused StatsUserWordProgress type
// Removed unused TimelineUserWordProgress type
